import type { QueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

/**
 * Query-cache key holding the current Lead's id. Lives in the TanStack Query
 * cache (reaching sessionStorage only via the query persister), not in
 * sessionStorage directly. The lead query's identity — see ADR 013.
 */
export const LEAD_ID_STORAGE_KEY = 'partners::ssu::leadId' as const;

/**
 * First segment of the lead query key. The full key is
 * `[LEAD_STATE_STORAGE_KEY, { leadId }]` — see `getLeadQueryKey`.
 */
export const LEAD_STATE_STORAGE_KEY = 'partners::ssu::session::lead' as const;

/**
 * LeadId kept after signup lead queries are cleared so `/opportunity/` can retry
 * searchOpp. Intentionally excluded from {@link clearLeadStorage}.
 */
const OPPORTUNITY_PENDING_LEAD_ID_KEY =
  'partners::ssu::opportunityPendingLeadId' as const;

/**
 * @constant {string} SSU_SESSION_ID - Key for storing the session ID in session storage.
 */
export const SSU_SESSION_ID = 'partners::ssu::sessionId';

/**
 * @constant {string} OTP_DISCRIMINATOR - Key for the Discriminator of the Email-validation challenge, in local
 * storage. It is shared by every tab of the browser and lasts across Leads, so it is intentionally excluded from
 * {@link clearSessionStorage}. It is not {@link SSU_SESSION_ID}: that one is for monitoring and flags.
 */
export const OTP_DISCRIMINATOR = 'partners::ssu::otpDiscriminator';

/**
 * @constant {string} START_FLOW_EXPERIMENT_USER_ID - Key for storing the start flow experiment user ID in local storage.
 */
export const START_FLOW_EXPERIMENT_USER_ID =
  'partners::ssu::startFlowExperimentUserId';

/**
 * @constant {string} EXPERIMENT_VARIANT_STORAGE_KEY - Key for storing decided experiment/toggle variants.
 */
export const EXPERIMENT_VARIANT_STORAGE_KEY =
  'partners::ssu::experimentVariant';

/**
 * Removes the Lead working copy and the Lead id from the query cache.
 */
export const clearLeadStorage = (queryClient: QueryClient) => {
  queryClient.removeQueries({
    predicate: ({ queryKey }) => {
      return (
        queryKey[0] === LEAD_ID_STORAGE_KEY ||
        queryKey[0] === LEAD_STATE_STORAGE_KEY
      );
    },
  });
};

/**
 * Removes all lead-related entries (lead state, leadId) from the query cache.
 * The challenge and the Verdict stay: the Verified email lasts for the tab (ADR 024).
 *
 * @param {QueryClient} queryClient - The query client instance.
 * @param {boolean} removeSessionId - Whether to also remove the session ID from sessionStorage - used for monitoring.
 */
export const clearSessionStorage = (
  queryClient: QueryClient,
  removeSessionId = true,
) => {
  clearLeadStorage(queryClient);

  if (removeSessionId) {
    sessionStorage.removeItem(SSU_SESSION_ID);
  }
};

export function getOpportunityPendingLeadId(
  queryClient: QueryClient,
): string | undefined {
  return queryClient.getQueryData<string>([OPPORTUNITY_PENDING_LEAD_ID_KEY]);
}

export function setOpportunityPendingLeadId(
  queryClient: QueryClient,
  leadId: string,
) {
  queryClient.setQueryData([OPPORTUNITY_PENDING_LEAD_ID_KEY], leadId);
}

export function clearOpportunityPendingLeadId(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: [OPPORTUNITY_PENDING_LEAD_ID_KEY] });
}

export const clearSubmissionState = (queryClient: QueryClient) => {
  clearOpportunityPendingLeadId(queryClient);
};

/**
 * Gets current storage keys.
 */
export const getStorageLeadKeys = (queryClient: QueryClient) => {
  const leadId = queryClient.getQueryData<string>([LEAD_ID_STORAGE_KEY]) ?? '';

  return { leadId };
};

type AllowedKeys = typeof LEAD_ID_STORAGE_KEY;

/**
 * Caches a query key-value pair in the query client.
 * // NOTE:
 *    This will be reactive if used with `useQuery` or `useSuspenseQuery` hooks.
 *    Make sure to evaluate if that choice is right based on your use case.
 * @param {QueryClient} queryClient - The query client instance to store the data in.
 * @param {Object} params - The parameters object.
 * @param {AllowedKeys} params.key - The storage key (must be one of the allowed keys).
 * @param {string} params.value - The value to cache.
 */
export const cacheQueryKey = (
  queryClient: QueryClient,
  { key, value }: { key: AllowedKeys; value: string },
) => {
  queryClient.setQueryData<string>([key], () => {
    return value;
  });
};

/**
 * Gets or creates a persistent user ID from localStorage for start flow experiments.
 * This ID persists across browser sessions and is used specifically for start flow experiment decisions.
 */
export const getStartFlowExperimentUserId = (): string => {
  const existing = localStorage.getItem(START_FLOW_EXPERIMENT_USER_ID);
  if (existing) return existing;

  const newUserId = uuidv4();
  localStorage.setItem(START_FLOW_EXPERIMENT_USER_ID, newUserId);
  return newUserId;
};
